import React, { Fragment, useEffect, useState } from "react";
import { FormHeading } from "../../../../ui/shared/components/Typograhy";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { moseOverHike, selectHike } from "../../../../data/booking/actions/map";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {getHikeList, getThemeList} from "../../../../data/booking/selectors/hike";
import { fetchThemes } from "../../../../data/booking/actions/booking";
import RouteSearchInput from "./RouteSearchInput";
import RouteCard from "./RouteCard";
import { Breadcrumb } from "../form/Breadcrumb";
import {BackButton, ButtonArea, NextButton} from "../../../../ui/shared/components/Buttons";
import RoutesFilter from "../../../../ui/booking/components/choose_route/RoutesFilter";
import Fuse from "fuse.js";
import cx from "classnames";

function filterPaths(pathList, selectedThemes, searchString, orderBy, selectedHike) {
    let newPathList = pathList;

    // Filters by selected hike
    if (selectedHike) {
        newPathList = newPathList.filter((path) => {
            return path.id == selectedHike.id;
        });

        // If user has selected a hike, we don't need to bother about filtering
        return newPathList;
    }

    // Filters by themes
    if (selectedThemes.length > 0) {
        newPathList = newPathList.filter((hike) => {
            return selectedThemes.some((theme) => theme.hikes.includes(hike.id));
        });
    }

    // Filters by search input
    if (searchString.length > 0) {
        const fuse = new Fuse(newPathList.map(item => (
            {
                ...item,
                titleNormalized: item.title.normalize("NFD").replace(/\p{Diacritic}/gu, "")
            })), {
            threshold: 0.3,
            keys: ["title", "titleNormalized", "tags.value"],
        });
        newPathList = fuse.search(searchString).map((item) => item.item);
    }

    // Order by
    if (orderBy === "popularity") {
        newPathList = newPathList.sort((a, b) => {
            return b.popularity - a.popularity;
        });
    } else if (orderBy === "alpha") {
        newPathList = newPathList.sort((a, b) => {
            return a.title.trim().localeCompare(b.title.trim());
        });
    } else if (orderBy === "tag") {
        newPathList = newPathList.sort((a, b) => {
            return (a.tags.length > 0 ? a.tags[0].value : "").localeCompare(
                b.tags.length > 0 ? b.tags[0].value : ""
            );
        });
    }
    return newPathList;
}

const ChooseRoute = ({ onSelectRoute }) => {
    const selectedHike = useSelector((state) => state.booking.map.hike);
    const loadingSteps =
        useSelector((state) => state.booking.map.loadingSteps).length > 0;
    const isLoadingThemes = useSelector((state) => state.booking.map.isFetchingThemes);
    const pathList = useSelector((state) => getHikeList(state));
    const themes = useSelector((state) => getThemeList(state));
    const dispatch = useDispatch();
    const [filteredPaths, setFilteredPaths] = useState(null);
    const [selectedThemes, setSelectedThemes] = useState([]);
    const [searchString, setSearchString] = useState("");
    const [orderBy, setOrderBy] = useState("popularity");
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if(isEmpty(themes) && !isLoadingThemes) {
            dispatch(fetchThemes(i18n.language));
        }
    }, []);

    useEffect(() => {
        const result = filterPaths(
            pathList,
            selectedThemes,
            searchString,
            orderBy,
            selectedHike
        );
        setFilteredPaths(result);
    }, [searchString, orderBy, selectedThemes.length, pathList.length, selectedHike]);

    function handlePathClick(hike = null) {
        let currentTheme = selectedThemes.length > 0 ? selectedThemes[0] : null;
        if (hike == null || (selectedHike && selectedHike.id == hike.id)) {
            dispatch(selectHike(null, currentTheme));
        } else {
            dispatch(selectHike(hike, currentTheme));
        }
    }

    return (
        <>
            <Breadcrumb />

            <FormHeading
                title={t("booking.select_path")}
                subtitle={
                    <>
                        {t("booking.path_not_found")}{" "}
                        <a
                            href={generatePath("/:locale/contact", {
                                locale: i18n.language,
                            })}
                        >
                            {t("booking.contact_us")}
                        </a>
                    </>
                }
            />

            <div className={"mt-8 mb-6"}>
                <RouteSearchInput setSearchString={setSearchString} />
            </div>
            <div className={"mb-3"}>
                <RoutesFilter
                    selectedThemes={selectedThemes}
                    setSelectedThemes={setSelectedThemes}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                />
            </div>

            {filteredPaths?.length > 0 && (
                <div className={"grid grid-cols-2 gap-4 xl:grid-cols-3 xl:gap-8"}>
                    {filteredPaths.map((hike, index) => {
                        return (
                            <Fragment key={index}>
                                <div
                                    key={index}
                                    onClick={() => handlePathClick(hike)}
                                    onMouseOver={() => dispatch(moseOverHike(hike))}
                                    onMouseOut={() => dispatch(moseOverHike(null))}
                                    className={cx({
                                        "col-span-full":
                                            selectedHike &&
                                            hike.id == selectedHike.id,
                                    })}
                                >
                                    <RouteCard
                                        hike={hike}
                                        isSelected={
                                            selectedHike && hike.id == selectedHike.id
                                        }
                                    />
                                </div>
                            </Fragment>
                        );
                    })}
                </div>
            )}

            {!filteredPaths ||
                (filteredPaths.length == 0 && (
                    <div className={"mt-8 w-full py-16 text-center"}>
                        <p className={"text-xl font-bold"}>Aucun parcours trouvé</p>
                        <p className={"text-gray-500"}>
                            Essayez une autre recherche ou réinitialisez les filtres.
                            <br />
                            Si votre randonnée n'est pas disponible, contactez-nous au 04 71 04 21 79
                        </p>
                    </div>
                ))}

            <ButtonArea>
                {selectedHike && <BackButton onClick={()=>handlePathClick()}>
                    {t("booking.back")}
                </BackButton>}
                <NextButton
                    classname="ml-auto"
                    loading={loadingSteps}
                    disabled={loadingSteps || isEmpty(selectedHike)}
                    onClick={() => onSelectRoute(selectedHike)}
                >
                    {t("booking.next")}
                </NextButton>
            </ButtonArea>
        </>
    );
};

export default ChooseRoute;
